import { useCallback, useEffect, useRef, useState } from "react";
import {
  playCancelSound,
  playHapticFeedback,
  playStartRecordingSound,
  playStopRecordingSound,
} from "../components/chat/whatsapp-audio-recorder/audio-effects";
import { formatVoiceClock } from "./useVoiceToText";

const getAudioMimeType = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafari =
    (ua.includes("safari") && !ua.includes("chrome")) ||
    ua.includes("iphone") ||
    ua.includes("ipad");

  const mimeTypes = isSafari
    ? [
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/mpeg",
        "audio/webm;codecs=opus",
        "audio/webm",
      ]
    : [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/ogg",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
      ];

  for (const type of mimeTypes) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return type;
    } catch {
      // ignore
    }
  }
  return "";
};

/**
 * Blob.type must match the real MediaRecorder container.
 * iOS/Safari records AAC-in-MP4; labeling it as audio/mp3 made the backend
 * decode with the wrong format → "Invalid or unsupported audio".
 */
const getBlobType = (recorderMimeType: string): string => {
  const raw = (recorderMimeType || "").split(";")[0].trim().toLowerCase();
  if (raw.includes("mp4") || raw.includes("m4a") || raw.includes("aac")) {
    return "audio/mp4";
  }
  if (raw.includes("mpeg") || raw === "audio/mp3") return "audio/mpeg";
  if (raw.includes("ogg")) return "audio/ogg";
  if (raw.includes("wav")) return "audio/wav";
  if (raw.includes("webm")) return "audio/webm";

  const ua = navigator.userAgent.toLowerCase();
  const isApple =
    ua.includes("iphone") ||
    ua.includes("ipad") ||
    ua.includes("ipod") ||
    (ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android"));
  if (isApple) return "audio/mp4";

  return raw || "audio/webm";
};

interface UseAsyncVoiceRecorderOptions {
  maxSeconds?: number;
  onComplete: (blob: Blob) => void | Promise<void>;
}

export const useAsyncVoiceRecorder = ({
  maxSeconds = 120,
  onComplete,
}: UseAsyncVoiceRecorderOptions) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordTimeMs, setRecordTimeMs] = useState(0);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [pausedPreviewUrl, setPausedPreviewUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);
  const cancelledRef = useRef(false);
  const startInFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const accumulatedMsRef = useRef(0);
  const pausedPreviewUrlRef = useRef<string | null>(null);
  const onCompleteRef = useRef(onComplete);
  const maxSecondsRef = useRef(maxSeconds);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    maxSecondsRef.current = maxSeconds;
  }, [maxSeconds]);

  const clearRecordTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    recordingStartedAtRef.current = null;
  }, []);

  const syncTimerDisplay = useCallback(() => {
    const live =
      recordingStartedAtRef.current != null && !isPausedRef.current
        ? Date.now() - recordingStartedAtRef.current
        : 0;
    const total = accumulatedMsRef.current + live;
    setRecordTimeMs(total);
    if (total >= maxSecondsRef.current * 1000) {
      // Auto-stop at max duration (send path).
      if (
        mediaRecorderRef.current &&
        (mediaRecorderRef.current.state === "recording" ||
          mediaRecorderRef.current.state === "paused")
      ) {
        playStopRecordingSound();
        playHapticFeedback();
        mediaRecorderRef.current.stop();
      }
    }
  }, []);

  const startRecordTimer = useCallback(
    (reset: boolean) => {
      clearRecordTimer();
      if (reset) {
        accumulatedMsRef.current = 0;
        setRecordTimeMs(0);
      }
      recordingStartedAtRef.current = Date.now();
      timerRef.current = setInterval(syncTimerDisplay, 50);
    },
    [clearRecordTimer, syncTimerDisplay],
  );

  const pauseRecordTimer = useCallback(() => {
    if (recordingStartedAtRef.current != null) {
      accumulatedMsRef.current += Date.now() - recordingStartedAtRef.current;
      recordingStartedAtRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecordTimeMs(accumulatedMsRef.current);
  }, []);

  const revokePausedPreview = useCallback(() => {
    if (pausedPreviewUrlRef.current) {
      URL.revokeObjectURL(pausedPreviewUrlRef.current);
      pausedPreviewUrlRef.current = null;
    }
    setPausedPreviewUrl(null);
  }, []);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setMediaStream(null);
  }, []);

  const buildPreviewFromChunks = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || audioChunksRef.current.length === 0) return null;
    const blob = new Blob(audioChunksRef.current, {
      type: getBlobType(recorder.mimeType),
    });
    if (blob.size === 0) return null;
    return URL.createObjectURL(blob);
  }, []);

  const startRecording = useCallback(async () => {
    if (isRecordingRef.current || startInFlightRef.current) return;

    startInFlightRef.current = true;
    cancelledRef.current = false;

    try {
      revokePausedPreview();
      isPausedRef.current = false;
      setIsPaused(false);
      playStartRecordingSound();
      playHapticFeedback();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (cancelledRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        cancelledRef.current = false;
        return;
      }

      streamRef.current = stream;
      setMediaStream(stream);

      const mimeType = getAudioMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const wasCancelled = cancelledRef.current;
        cancelledRef.current = false;
        clearRecordTimer();
        accumulatedMsRef.current = 0;
        isRecordingRef.current = false;
        isPausedRef.current = false;
        setIsRecording(false);
        setIsPaused(false);
        cleanupStream();
        revokePausedPreview();

        if (wasCancelled) {
          audioChunksRef.current = [];
          setRecordTimeMs(0);
          return;
        }

        const blob = new Blob(audioChunksRef.current, {
          type: getBlobType(recorder.mimeType),
        });
        audioChunksRef.current = [];
        setRecordTimeMs(0);
        if (blob.size === 0) return;
        void onCompleteRef.current(blob);
      };

      recorder.start(250);
      isRecordingRef.current = true;
      setIsRecording(true);
      startRecordTimer(true);
    } catch (error) {
      console.error("Cannot start async voice recording:", error);
      cleanupStream();
      clearRecordTimer();
      accumulatedMsRef.current = 0;
      isRecordingRef.current = false;
      isPausedRef.current = false;
      setIsRecording(false);
      setIsPaused(false);
      throw error;
    } finally {
      startInFlightRef.current = false;
    }
  }, [
    cleanupStream,
    clearRecordTimer,
    revokePausedPreview,
    startRecordTimer,
  ]);

  const stopRecording = useCallback(() => {
    const state = mediaRecorderRef.current?.state;
    if (state !== "recording" && state !== "paused") return;
    clearRecordTimer();
    revokePausedPreview();
    isPausedRef.current = false;
    setIsPaused(false);
    playStopRecordingSound();
    playHapticFeedback();
    mediaRecorderRef.current?.stop();
  }, [clearRecordTimer, revokePausedPreview]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    try {
      recorder.requestData();
    } catch {
      // ignore
    }
    recorder.pause();
    pauseRecordTimer();
    isPausedRef.current = true;
    setIsPaused(true);
    playHapticFeedback();
    window.setTimeout(() => {
      if (!isPausedRef.current) return;
      revokePausedPreview();
      const previewUrl = buildPreviewFromChunks();
      if (previewUrl) {
        pausedPreviewUrlRef.current = previewUrl;
        setPausedPreviewUrl(previewUrl);
      }
    }, 80);
  }, [buildPreviewFromChunks, pauseRecordTimer, revokePausedPreview]);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    revokePausedPreview();
    recorder.resume();
    isPausedRef.current = false;
    setIsPaused(false);
    startRecordTimer(false);
    playHapticFeedback();
  }, [revokePausedPreview, startRecordTimer]);

  const cancelRecording = useCallback(() => {
    const state = mediaRecorderRef.current?.state;
    const canCancel =
      isRecordingRef.current ||
      startInFlightRef.current ||
      state === "recording" ||
      state === "paused";
    if (!canCancel) return;

    cancelledRef.current = true;
    clearRecordTimer();
    accumulatedMsRef.current = 0;
    setRecordTimeMs(0);
    revokePausedPreview();
    isPausedRef.current = false;
    setIsPaused(false);
    playCancelSound();
    playHapticFeedback();

    if (state === "recording" || state === "paused") {
      mediaRecorderRef.current?.stop();
      return;
    }

    cleanupStream();
    isRecordingRef.current = false;
    setIsRecording(false);
    if (!startInFlightRef.current) {
      cancelledRef.current = false;
    }
  }, [cleanupStream, clearRecordTimer, revokePausedPreview]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      const state = mediaRecorderRef.current?.state;
      if (state === "recording" || state === "paused") {
        mediaRecorderRef.current?.stop();
      }
      clearRecordTimer();
      cleanupStream();
      revokePausedPreview();
    };
  }, [cleanupStream, clearRecordTimer, revokePausedPreview]);

  return {
    isRecording,
    isPaused,
    recordTimeMs,
    mediaStream,
    pausedPreviewUrl,
    formattedClockTime: formatVoiceClock(recordTimeMs),
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  };
};
